import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { signOut, useSession } from '@ghq-abi/auth-client-lib/client';
import { useTranslate } from '@tolgee/react';
import nprogress from 'nprogress';

import { useAbility } from '~/app/contexts/AbilityContext';
import { User } from '~/app/contexts/UserContext';
import { useGetProfilePicture } from '~/entities/Employee/api/useGetProfilePicture';
import { Icon } from '~/shared/components';
import { SighOut } from '~/shared/components/icons/SignOut';

type UseUserDropdownProps = {
  user: User;
};

export const useUserDropdown = ({ user }: UseUserDropdownProps) => {
  const { t } = useTranslate('web-app');
  const router = useRouter();
  const { proxyOut } = useSession();

  const isProxying = user.isProxied;

  const ability = useAbility();

  async function handleSignOut() {
    signOut({ redirect: false });
  }

  const goToProxy = useCallback(async () => {
    router.push('/proxy');
  }, [router]);

  const handleStopProxy = useCallback(async () => {
    nprogress.start();
    await proxyOut();
    window.location.reload();
  }, [proxyOut]);

  const items = useMemo(() => {
    const dropdownItems = [
      {
        label: t('common.sign_out'),
        onSelect: handleSignOut,
        icon: <SighOut />,
      },
    ];

    if (ability.can('proxy', 'User')) {
      dropdownItems.unshift({
        label: t(isProxying ? 'common.stop_proxy' : 'common.proxy_as_user'),
        onSelect: isProxying ? handleStopProxy : goToProxy,
        icon: isProxying ? (
          <Icon.StopProxy size={18} />
        ) : (
          <Icon.Proxy size={18} />
        ),
      });
    }

    return dropdownItems;
  }, [ability, goToProxy, handleStopProxy, isProxying, t]);

  const { data } = useGetProfilePicture(user.globalId ?? '');
  const imageSrc = data ? URL.createObjectURL(data) : undefined;

  const profileInfo = useMemo(() => {
    return {
      name: user.name ?? '',
      urlImage: imageSrc,
      zone: user.zone ?? '',
    };
  }, [imageSrc, user.name, user.zone]);

  return {
    items,
    profileInfo,
  };
};
